import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { adminAPI } from '../../services/api';
import { Plus, Edit2, Trash2, Loader2, Star, DollarSign } from 'lucide-react';
import StarRating from '../../components/store/StarRating';
import { toast } from '../../components/common/Toast';

const UNIT_OPTIONS = ['Unidad', 'Kg', 'Lt', 'Gr', 'Ml', 'Caja', 'Paquete', 'Botella', 'Sobre', 'Bolsa', 'Galón'];

const AdminProducts = () => {
  const { isDarkMode } = useTheme();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', short_description: '', description: '', price: 0, vu: 0,
    star_rating: '1', is_active: true, is_physical: true, stock_quantity: 0,
    category_id: '', subcategory_id: '', family_id: '',
    image_url: '', is_featured: false,
    sku: '', barcode: '', buy_unit: '', sell_unit: '', qty_per_case: 1,
    tags: '', expiration_control: false,
  });

  const load = async () => {
    try {
      const [prodRes, catRes, subRes, famRes] = await Promise.all([
        adminAPI.getProducts({ skip: 0, limit: 200 }),
        adminAPI.getCategories(),
        adminAPI.getSubcategories(),
        adminAPI.getFamilies(),
      ]);
      setProducts(prodRes.data);
      setCategories(catRes.data);
      setSubcategories(subRes.data);
      setFamilies(famRes.data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filteredSub = subcategories.filter(s => !form.category_id || s.category_id === parseInt(form.category_id));
  const filteredFam = families.filter(f => !form.subcategory_id || f.subcategory_id === parseInt(form.subcategory_id));

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', short_description: '', description: '', price: 0, vu: 0, star_rating: '1', is_active: true, is_physical: true, stock_quantity: 0, category_id: '', subcategory_id: '', family_id: '', image_url: '', is_featured: false, sku: '', barcode: '', buy_unit: '', sell_unit: '', qty_per_case: 1, tags: '', expiration_control: false });
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, short_description: p.short_description || '', description: p.description || '',
      price: p.price, vu: p.vu, star_rating: p.star_rating, is_active: p.is_active,
      is_physical: p.is_physical, stock_quantity: p.stock_quantity,
      category_id: p.category_id || '', subcategory_id: p.subcategory_id || '', family_id: p.family_id || '',
      image_url: p.image_url || '', is_featured: p.is_featured || false,
      sku: p.sku || '', barcode: p.barcode || '', buy_unit: p.buy_unit || '', sell_unit: p.sell_unit || '',
      qty_per_case: p.qty_per_case || 1, tags: (p.tags || []).join(', '), expiration_control: p.expiration_control || false,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        ...form,
        category_id: form.category_id ? parseInt(form.category_id) : null,
        subcategory_id: form.subcategory_id ? parseInt(form.subcategory_id) : null,
        family_id: form.family_id ? parseInt(form.family_id) : null,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        sku: form.sku || undefined,
      };
      if (editing) {
        await adminAPI.updateProduct(editing.id, data);
        toast.success('Producto actualizado');
      } else {
        await adminAPI.createProduct(data);
        toast.success('Producto creado');
      }
      setShowModal(false);
      await load();
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    try { await adminAPI.deleteProduct(id); toast.success('Producto eliminado'); await load(); } catch {}
  };

  const getCategoryName = (id) => categories.find((c) => c.id === id)?.name || '—';
  const getSubcatName = (id) => subcategories.find((s) => s.id === id)?.name || '—';

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Productos</h1>
          <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Catálogo completo con SKU, categoría jerárquica y VU</p>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-xl hover:bg-indigo-700">
          <Plus className="w-4 h-4" /> Nuevo Producto
        </button>
      </div>

      <div className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl p-6 border shadow-sm overflow-x-auto`}>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <th className="text-left py-3 px-3">Producto</th>
                <th className="text-left py-3 px-3">SKU</th>
                <th className="text-left py-3 px-3">Categoría</th>
                <th className="text-left py-3 px-3">Subcategoría</th>
                <th className="text-right py-3 px-3">Precio</th>
                <th className="text-right py-3 px-3">VU</th>
                <th className="text-center py-3 px-3">Estrellas</th>
                <th className="text-center py-3 px-3">Activo</th>
                <th className="text-right py-3 px-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className={`border-b ${isDarkMode ? 'border-gray-700/50' : 'border-gray-100'} hover:${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-3">
                      {p.image_url ? (
                        <img src={p.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded-lg flex items-center justify-center">
                          <DollarSign className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                      <span className="font-medium truncate max-w-[180px]">{p.name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-3 font-mono text-xs">{p.sku || '—'}</td>
                  <td className="py-3 px-3 text-gray-500">{getCategoryName(p.category_id)}</td>
                  <td className="py-3 px-3 text-gray-500">{getSubcatName(p.subcategory_id)}</td>
                  <td className="py-3 px-3 text-right font-medium">${p.price?.toFixed(2)}</td>
                  <td className="py-3 px-3 text-right text-gray-500">${p.vu?.toFixed(2)}</td>
                  <td className="py-3 px-3 text-center"><StarRating rating={p.star_rating} size="sm" /></td>
                  <td className="py-3 px-3 text-center">
                    <span className={`w-2 h-2 rounded-full inline-block ${p.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                  </td>
                  <td className="py-3 px-3 text-right">
                    <button onClick={() => openEdit(p)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg mr-1"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(p.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 max-w-3xl w-full shadow-2xl max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4">{editing ? 'Editar Producto' : 'Nuevo Producto'}</h2>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-3">
                <label className="text-sm text-gray-500">Nombre *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600" />
              </div>
              <div className="col-span-3">
                <label className="text-sm text-gray-500">Descripción Corta</label>
                <input value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600" />
              </div>
              <div className="col-span-3">
                <label className="text-sm text-gray-500">Descripción</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600" rows={2} />
              </div>
              <div className="col-span-2">
                <label className="text-sm text-gray-500">SKU (opcional — auto-generado si se omite)</label>
                <input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 font-mono text-sm" placeholder="STT-ALI-000001-FAM" />
              </div>
              <div>
                <label className="text-sm text-gray-500">Código de Barras</label>
                <input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600" />
              </div>
              <div className="col-span-3 border-b border-gray-200 dark:border-gray-600 pb-2"><span className="text-sm font-semibold">Jerarquía</span></div>
              <div>
                <label className="text-sm text-gray-500">Categoría</label>
                <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value, subcategory_id: '', family_id: '' })} className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600">
                  <option value="">Sin categoría</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-500">Subcategoría</label>
                <select value={form.subcategory_id} onChange={(e) => setForm({ ...form, subcategory_id: e.target.value, family_id: '' })} className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600">
                  <option value="">Sin subcategoría</option>
                  {filteredSub.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-500">Familia</label>
                <select value={form.family_id} onChange={(e) => setForm({ ...form, family_id: e.target.value })} className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600">
                  <option value="">Sin familia</option>
                  {filteredFam.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>
              <div className="col-span-3 border-b border-gray-200 dark:border-gray-600 pb-2"><span className="text-sm font-semibold">Precios y Unidades</span></div>
              <div>
                <label className="text-sm text-gray-500">Precio (USD) *</label>
                <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600" />
              </div>
              <div>
                <label className="text-sm text-gray-500">VU (Vol. Utilizable)</label>
                <input type="number" step="0.01" value={form.vu} onChange={(e) => setForm({ ...form, vu: parseFloat(e.target.value) || 0 })} className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600" />
              </div>
              <div>
                <label className="text-sm text-gray-500">Unidad de Compra</label>
                <select value={form.buy_unit} onChange={(e) => setForm({ ...form, buy_unit: e.target.value })} className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600">
                  <option value="">Seleccionar</option>
                  {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-500">Unidad de Venta</label>
                <select value={form.sell_unit} onChange={(e) => setForm({ ...form, sell_unit: e.target.value })} className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600">
                  <option value="">Seleccionar</option>
                  {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-500">Cantidad por Caja</label>
                <input type="number" min={1} value={form.qty_per_case} onChange={(e) => setForm({ ...form, qty_per_case: parseInt(e.target.value) || 1 })} className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600" />
              </div>
              <div className="col-span-3 border-b border-gray-200 dark:border-gray-600 pb-2"><span className="text-sm font-semibold">Configuración</span></div>
              <div>
                <label className="text-sm text-gray-500">Star Rating</label>
                <select value={form.star_rating} onChange={(e) => setForm({ ...form, star_rating: e.target.value })} className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600">
                  <option value="1">1 Estrella</option>
                  <option value="2">2 Estrellas</option>
                  <option value="3">3 Estrellas</option>
                  <option value="4">4 Estrellas</option>
                  <option value="5">5 Estrellas</option>
                  <option value="top">Top Star</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-500">Stock</label>
                <input type="number" value={form.stock_quantity} onChange={(e) => setForm({ ...form, stock_quantity: parseInt(e.target.value) || 0 })} className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600" />
              </div>
              <div className="col-span-2">
                <label className="text-sm text-gray-500">Tags (separados por coma)</label>
                <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600" placeholder="orgánico, sin azúcar, vegano" />
              </div>
              <div>
                <label className="text-sm text-gray-500">URL Imagen</label>
                <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600" />
              </div>
              <div className="flex items-center gap-6 col-span-3">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.is_physical} onChange={(e) => setForm({ ...form, is_physical: e.target.checked })} />
                  Producto Físico
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                  Activo
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} />
                  Destacado
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.expiration_control} onChange={(e) => setForm({ ...form, expiration_control: e.target.checked })} />
                  Control de Vencimiento
                </label>
              </div>
            </div>
            <div className="flex gap-3 pt-4 mt-4 border-t">
              <button onClick={() => setShowModal(false)} className="flex-1 py-3 border rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700">Cancelar</button>
              <button onClick={handleSave} disabled={saving || !form.name || !form.price} className="flex-1 bg-indigo-600 text-white py-3 rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {editing ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default AdminProducts;
